import * as THREE from "three";

/**
 * Private bedroom / home-office — lived-in remote-work apartment.
 * Matches the "private room" quality bar (bed + dual-monitor desk + lounge),
 * not a graybox kitchen table. Waypoints:
 *   wp_apt_establish — wide room (bed left, desk back wall)
 *   wp_apt_desk — push in on dual monitors (~[0, 0.95, -1.35])
 *
 * Hotspot props (must stay visible at desk cam):
 *   sticky note on left monitor bezel
 *   USB stick on desk right of keyboard
 *   backpack on floor right of chair
 */
export function Apartment() {
  return (
    <group>
      {/* wood floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[14, 12]} />
        <meshStandardMaterial color="#c4a882" roughness={0.88} />
      </mesh>
      {/* blue area rug */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0.2, 0.012, 0.6]} receiveShadow>
        <planeGeometry args={[3.4, 2.6]} />
        <meshStandardMaterial color="#5a7a9a" roughness={1} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0.2, 0.014, 0.6]}>
        <planeGeometry args={[3.55, 2.75]} />
        <meshStandardMaterial color="#d0d6dc" roughness={1} transparent opacity={0.35} />
      </mesh>

      {/* walls */}
      <mesh position={[0, 1.7, -2.85]} receiveShadow>
        <boxGeometry args={[12, 3.4, 0.14]} />
        <meshStandardMaterial color="#ebe6dc" roughness={0.96} />
      </mesh>
      <mesh position={[-3.6, 1.7, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <boxGeometry args={[8, 3.4, 0.14]} />
        <meshStandardMaterial color="#e4dfd4" roughness={0.96} />
      </mesh>
      <mesh position={[3.6, 1.7, 0]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
        <boxGeometry args={[8, 3.4, 0.14]} />
        <meshStandardMaterial color="#e4dfd4" roughness={0.96} />
      </mesh>
      {/* ceiling */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 3.35, 0]}>
        <planeGeometry args={[12, 10]} />
        <meshStandardMaterial color="#f2efe8" roughness={1} />
      </mesh>

      <Bed />
      <Nightstand />
      <DeskSetup />
      <OfficeChair />
      <FilingCabinet />
      <FloatingShelves />
      <WallArt />
      <Beanbag />
      <Guitar />
      <CoffeeTableRouter />
      <Whiteboard />
      <WindowAC />
      <Plants />
      <Backpack />
      <UsbStick />
      <StickyNote />

      {/* soft daylight + warm desk lamp pool */}
      <hemisphereLight args={["#f5f0e6", "#8a7a68", 0.55]} />
      <directionalLight position={[2.5, 4, 1.5]} intensity={0.85} color="#fff6e8" castShadow />
      <pointLight position={[0.15, 1.55, -1.55]} intensity={0.9} distance={4} color="#ffe8b8" />
      <pointLight position={[-2.2, 2.2, 0.4]} intensity={0.35} distance={6} color="#e8f0ff" />
    </group>
  );
}

function Bed() {
  return (
    <group position={[-2.15, 0, 0.35]}>
      <mesh position={[0, 0.28, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.7, 0.28, 2.1]} />
        <meshStandardMaterial color="#2a3544" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.48, 0]} castShadow>
        <boxGeometry args={[1.65, 0.14, 2.0]} />
        <meshStandardMaterial color="#f4f2ee" roughness={0.95} />
      </mesh>
      {/* pillows */}
      {[-0.4, 0.05, 0.45].map((x, i) => (
        <mesh key={i} position={[x, 0.62, -0.75]} castShadow>
          <boxGeometry args={[0.38, 0.16, 0.28]} />
          <meshStandardMaterial color="#1e3a5f" roughness={0.92} />
        </mesh>
      ))}
      {/* headboard */}
      <mesh position={[0, 0.85, -1.0]} castShadow>
        <boxGeometry args={[1.7, 0.7, 0.08]} />
        <meshStandardMaterial color="#243040" roughness={0.85} />
      </mesh>
    </group>
  );
}

function Nightstand() {
  return (
    <group position={[-1.05, 0, -0.55]}>
      <mesh position={[0, 0.32, 0]} castShadow>
        <boxGeometry args={[0.42, 0.48, 0.38]} />
        <meshStandardMaterial color="#4a3424" roughness={0.7} />
      </mesh>
      {/* small open laptop on nightstand */}
      <group position={[0.02, 0.58, 0.02]}>
        <mesh castShadow>
          <boxGeometry args={[0.22, 0.012, 0.15]} />
          <meshStandardMaterial color="#2a3038" metalness={0.35} roughness={0.45} />
        </mesh>
        <mesh position={[0, 0.08, -0.06]} rotation={[-0.5, 0, 0]}>
          <boxGeometry args={[0.22, 0.14, 0.01]} />
          <meshStandardMaterial color="#1a1e24" />
        </mesh>
      </group>
    </group>
  );
}

function DeskSetup() {
  return (
    <group position={[0.15, 0, -1.55]}>
      {/* long dark desk */}
      <mesh position={[0, 0.74, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.4, 0.06, 0.72]} />
        <meshStandardMaterial color="#3d2a1c" roughness={0.55} metalness={0.08} />
      </mesh>
      {[
        [-1.05, -0.28],
        [1.05, -0.28],
        [-1.05, 0.28],
        [1.05, 0.28],
      ].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.37, z]} castShadow>
          <boxGeometry args={[0.07, 0.74, 0.07]} />
          <meshStandardMaterial color="#2e2016" roughness={0.7} />
        </mesh>
      ))}

      {/* dual monitors — XP-green hills vibe */}
      <Monitor x={-0.38} />
      <Monitor x={0.42} />

      {/* keyboard + mouse */}
      <mesh position={[0.02, 0.78, 0.18]} castShadow>
        <boxGeometry args={[0.42, 0.02, 0.14]} />
        <meshStandardMaterial color="#1a1e24" roughness={0.6} />
      </mesh>
      <mesh position={[0.38, 0.78, 0.2]} castShadow>
        <boxGeometry args={[0.06, 0.025, 0.09]} />
        <meshStandardMaterial color="#222830" roughness={0.5} />
      </mesh>

      {/* desk lamp */}
      <group position={[-0.95, 0.77, 0.1]}>
        <mesh castShadow>
          <cylinderGeometry args={[0.05, 0.06, 0.03, 12]} />
          <meshStandardMaterial color="#2a2a2a" metalness={0.5} />
        </mesh>
        <mesh position={[0, 0.22, 0]} castShadow>
          <cylinderGeometry args={[0.012, 0.012, 0.4, 8]} />
          <meshStandardMaterial color="#3a3a3a" metalness={0.5} />
        </mesh>
        <mesh position={[0.08, 0.38, 0.05]} rotation={[0.4, 0, 0.3]}>
          <coneGeometry args={[0.08, 0.12, 12, 1, true]} />
          <meshStandardMaterial
            color="#f5e6c8"
            emissive={new THREE.Color("#ffd090")}
            emissiveIntensity={0.55}
            side={THREE.DoubleSide}
          />
        </mesh>
      </group>

      {/* PC tower under desk */}
      <mesh position={[0.95, 0.32, 0.05]} castShadow>
        <boxGeometry args={[0.22, 0.5, 0.42]} />
        <meshStandardMaterial color="#1c1f24" roughness={0.55} metalness={0.25} />
      </mesh>
      <mesh position={[0.95, 0.45, 0.27]}>
        <planeGeometry args={[0.08, 0.04]} />
        <meshStandardMaterial color="#3ecf8e" emissive="#2aa86e" emissiveIntensity={0.7} />
      </mesh>
    </group>
  );
}

function Monitor({ x }: { x: number }) {
  return (
    <group position={[x, 0.77, -0.12]}>
      <mesh position={[0, 0.28, 0]} castShadow>
        <boxGeometry args={[0.62, 0.4, 0.04]} />
        <meshStandardMaterial color="#1a1e24" metalness={0.35} roughness={0.45} />
      </mesh>
      <mesh position={[0, 0.28, 0.022]}>
        <planeGeometry args={[0.56, 0.34]} />
        <meshStandardMaterial
          color="#5a9a3a"
          emissive={new THREE.Color("#3a7a28")}
          emissiveIntensity={0.45}
          roughness={0.35}
        />
      </mesh>
      {/* stand */}
      <mesh position={[0, 0.06, 0.02]}>
        <boxGeometry args={[0.08, 0.12, 0.06]} />
        <meshStandardMaterial color="#2a3038" />
      </mesh>
      <mesh position={[0, 0.01, 0.04]}>
        <boxGeometry args={[0.22, 0.02, 0.14]} />
        <meshStandardMaterial color="#2a3038" />
      </mesh>
    </group>
  );
}

function OfficeChair() {
  return (
    <group position={[0.2, 0, -0.85]}>
      <mesh position={[0, 0.48, 0]} castShadow>
        <boxGeometry args={[0.48, 0.08, 0.48]} />
        <meshStandardMaterial color="#1a2430" roughness={0.85} />
      </mesh>
      <mesh position={[0, 0.78, -0.18]} castShadow>
        <boxGeometry args={[0.48, 0.55, 0.08]} />
        <meshStandardMaterial color="#243040" roughness={0.85} />
      </mesh>
      {/* blue lumbar accent */}
      <mesh position={[0, 0.72, -0.14]}>
        <boxGeometry args={[0.36, 0.2, 0.04]} />
        <meshStandardMaterial color="#3a6a9a" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.28, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 0.4, 10]} />
        <meshStandardMaterial color="#2a2a2a" metalness={0.5} />
      </mesh>
      <mesh position={[0, 0.08, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.28, 0.28, 0.04, 16]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.4} />
      </mesh>
    </group>
  );
}

function FilingCabinet() {
  return (
    <group position={[-1.35, 0, -1.55]}>
      <mesh position={[0, 0.55, 0]} castShadow>
        <boxGeometry args={[0.45, 1.05, 0.5]} />
        <meshStandardMaterial color="#3a2a1c" roughness={0.65} />
      </mesh>
      {[0.25, 0.55, 0.85].map((y, i) => (
        <mesh key={i} position={[0, y, 0.26]}>
          <boxGeometry args={[0.38, 0.02, 0.01]} />
          <meshStandardMaterial color="#2a1e14" />
        </mesh>
      ))}
    </group>
  );
}

function FloatingShelves() {
  return (
    <group position={[0.15, 0, -2.72]}>
      {[1.85, 2.25].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} castShadow>
          <boxGeometry args={[2.2, 0.05, 0.22]} />
          <meshStandardMaterial color="#f0ebe3" roughness={0.9} />
        </mesh>
      ))}
      {/* books / boxes / plants on shelves */}
      {[
        [-0.8, 1.95, "#4a6a8a"],
        [-0.55, 1.95, "#8a4a4a"],
        [-0.3, 1.95, "#3a5a3a"],
        [0.5, 1.95, "#6a5a3a"],
        [0.75, 1.95, "#4a4a6a"],
        [-0.6, 2.35, "#5a7a5a"],
        [0.2, 2.35, "#8a6a4a"],
        [0.7, 2.35, "#3a6a8a"],
      ].map(([x, y, c], i) => (
        <mesh key={i} position={[x as number, y as number, 0.02]} castShadow>
          <boxGeometry args={[0.12, 0.18, 0.16]} />
          <meshStandardMaterial color={c as string} roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}

function WallArt() {
  const frames: [number, string][] = [
    [1.6, "#4a7ab8"],
    [0.7, "#c45a3a"],
    [-0.2, "#3a8a6a"],
  ];
  return (
    <group>
      {frames.map(([z, c], i) => (
        <group key={i} position={[-3.48, 1.85, z]} rotation={[0, Math.PI / 2, 0]}>
          <mesh>
            <boxGeometry args={[0.48, 0.4, 0.04]} />
            <meshStandardMaterial color="#2a2420" />
          </mesh>
          <mesh position={[0, 0, 0.025]}>
            <planeGeometry args={[0.4, 0.32]} />
            <meshStandardMaterial color={c} roughness={0.85} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function Beanbag() {
  return (
    <group position={[1.6, 0, 1.1]}>
      <mesh position={[0, 0.28, 0]} castShadow>
        <sphereGeometry args={[0.48, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.65]} />
        <meshStandardMaterial color="#6a9aba" roughness={0.95} />
      </mesh>
      <mesh position={[0.55, 0.12, 0.15]} castShadow>
        <boxGeometry args={[0.35, 0.18, 0.35]} />
        <meshStandardMaterial color="#5a8aaa" roughness={0.95} />
      </mesh>
    </group>
  );
}

function Guitar() {
  return (
    <group position={[1.35, 0.55, 1.35]} rotation={[0, 0.4, 0.15]}>
      <mesh castShadow>
        <boxGeometry args={[0.12, 0.85, 0.04]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.6} />
      </mesh>
      <mesh position={[0, -0.35, 0]} castShadow>
        <boxGeometry args={[0.28, 0.35, 0.06]} />
        <meshStandardMaterial color="#1c1c1c" roughness={0.55} metalness={0.2} />
      </mesh>
    </group>
  );
}

function CoffeeTableRouter() {
  return (
    <group position={[0.3, 0, 1.35]}>
      <mesh position={[0, 0.22, 0]} castShadow>
        <boxGeometry args={[0.7, 0.06, 0.4]} />
        <meshStandardMaterial color="#2a2018" roughness={0.6} />
      </mesh>
      {[
        [-0.28, -0.14],
        [0.28, -0.14],
        [-0.28, 0.14],
        [0.28, 0.14],
      ].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.11, z]}>
          <boxGeometry args={[0.04, 0.22, 0.04]} />
          <meshStandardMaterial color="#1e1812" />
        </mesh>
      ))}
      {/* white router */}
      <mesh position={[0, 0.28, 0]} castShadow>
        <boxGeometry args={[0.22, 0.05, 0.14]} />
        <meshStandardMaterial color="#e8e8e8" roughness={0.5} />
      </mesh>
      <mesh position={[-0.06, 0.4, 0]}>
        <cylinderGeometry args={[0.006, 0.006, 0.18, 6]} />
        <meshStandardMaterial color="#ccc" />
      </mesh>
      <mesh position={[0.06, 0.4, 0]}>
        <cylinderGeometry args={[0.006, 0.006, 0.18, 6]} />
        <meshStandardMaterial color="#ccc" />
      </mesh>
    </group>
  );
}

function Whiteboard() {
  return (
    <group position={[2.8, 1.1, 1.6]} rotation={[0, -0.5, 0]}>
      <mesh castShadow>
        <boxGeometry args={[0.08, 1.4, 1.0]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[-0.05, 0, 0]}>
        <boxGeometry args={[0.02, 1.2, 0.85]} />
        <meshStandardMaterial color="#6a9aba" roughness={0.7} />
      </mesh>
    </group>
  );
}

function WindowAC() {
  return (
    <group position={[3.48, 2.4, -0.8]}>
      <mesh>
        <boxGeometry args={[0.2, 0.35, 0.9]} />
        <meshStandardMaterial color="#d8dde3" metalness={0.3} roughness={0.5} />
      </mesh>
      {/* window */}
      <mesh position={[0, -0.9, 0.2]}>
        <planeGeometry args={[0.02, 1.2]} />
        <meshStandardMaterial
          color="#8eb4d4"
          emissive="#6a9abb"
          emissiveIntensity={0.4}
          roughness={0.25}
        />
      </mesh>
    </group>
  );
}

function Plants() {
  return (
    <group>
      {[
        [2.4, 0, -2.4],
        [-3.1, 0, 1.8],
        [1.0, 2.35, -2.55],
      ].map(([x, y, z], i) => (
        <group key={i} position={[x, y, z]}>
          <mesh position={[0, 0.12, 0]} castShadow>
            <cylinderGeometry args={[0.08, 0.1, 0.16, 10]} />
            <meshStandardMaterial color="#8a5a3a" roughness={0.9} />
          </mesh>
          <mesh position={[0, 0.32, 0]} castShadow>
            <sphereGeometry args={[0.14, 10, 10]} />
            <meshStandardMaterial color="#3a7a4a" roughness={0.95} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

/** Floor backpack — right of chair, readable silhouette. */
function Backpack() {
  return (
    <group position={[0.95, 0.28, -0.55]}>
      {/* body */}
      <mesh castShadow>
        <boxGeometry args={[0.38, 0.48, 0.26]} />
        <meshStandardMaterial color="#2a4a72" roughness={0.85} />
      </mesh>
      {/* top lid bulge */}
      <mesh position={[0, 0.28, 0]} castShadow>
        <boxGeometry args={[0.34, 0.12, 0.24]} />
        <meshStandardMaterial color="#1e3a5c" roughness={0.85} />
      </mesh>
      {/* front pocket */}
      <mesh position={[0, -0.02, 0.14]} castShadow>
        <boxGeometry args={[0.28, 0.22, 0.06]} />
        <meshStandardMaterial color="#3a5a82" roughness={0.85} />
      </mesh>
      {/* straps */}
      {[-0.1, 0.1].map((x, i) => (
        <mesh key={i} position={[x, 0.05, -0.14]}>
          <boxGeometry args={[0.06, 0.4, 0.03]} />
          <meshStandardMaterial color="#1a3048" />
        </mesh>
      ))}
    </group>
  );
}

/** USB stick on desk — oversized for readability at desk cam. */
function UsbStick() {
  return (
    <group position={[0.58, 0.79, -1.32]} rotation={[0, 0.55, 0]}>
      <mesh castShadow>
        <boxGeometry args={[0.14, 0.04, 0.05]} />
        <meshStandardMaterial color="#1a6ab0" metalness={0.35} roughness={0.4} />
      </mesh>
      <mesh position={[0.09, 0, 0]}>
        <boxGeometry args={[0.05, 0.028, 0.032]} />
        <meshStandardMaterial color="#d0d0d0" metalness={0.65} roughness={0.3} />
      </mesh>
      {/* label stripe */}
      <mesh position={[-0.02, 0.021, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.08, 0.03]} />
        <meshStandardMaterial color="#f0f0f0" />
      </mesh>
    </group>
  );
}

/** Sticky note on left monitor — large yellow square. */
function StickyNote() {
  return (
    <group position={[-0.52, 1.18, -1.62]}>
      <mesh castShadow>
        <planeGeometry args={[0.16, 0.16]} />
        <meshStandardMaterial color="#f5e36a" roughness={0.95} />
      </mesh>
      {/* fake scribble lines */}
      {[-0.03, 0, 0.03].map((y, i) => (
        <mesh key={i} position={[0, y, 0.001]}>
          <planeGeometry args={[0.1, 0.008]} />
          <meshStandardMaterial color="#6a5a20" />
        </mesh>
      ))}
    </group>
  );
}
